import { RepeatIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Grid, Input, Text } from "@chakra-ui/react";
import apiCarteira from "api/api-carteira";
import {
  bgError,
  bgSucess,
  bgWarning,
  StatusFormat
} from "components/dynamic-table/row-status";
import { openModalConfirm } from "components/modal-confirm-new";
import { SkeletonLoading } from "components/skeleton-loading";
import { Toast } from "components/toast";
import { useApplicationContext } from "contexts/ApplicationContext";
import { SelectedCustomer } from "pages/carteira/page";
import { Dispatch, SetStateAction, useRef } from "react";
import { PriceButton } from ".";
import { CardBgIcon } from "./icons";

export interface WhiteLabelData {
  ativo: boolean;
  dominio: string;
  expirationDate?: string;
  expired?: boolean;
}

export function WhiteLabelServiceCard({
  isLoading,
  title,
  priceDesc,
  serviceData,
  selectedCustomer,
  setCardData
}: {
  isLoading?: boolean;
  title: string;
  priceDesc: string;
  serviceData: any;
  selectedCustomer: SelectedCustomer;
  setCardData: Dispatch<
    SetStateAction<Partial<{ whiteLabel: WhiteLabelData }>>
  >;
}) {
  const { user } = useApplicationContext();
  const dominioRef = useRef<HTMLInputElement>(null);
  const px = "25px";
  const cardData: WhiteLabelData | undefined = serviceData?.["whiteLabel"];
  const isActive: boolean = !!cardData?.ativo;
  const isExpired: boolean = !!cardData?.expired;

  const canToggle = (
    [
      "SUPER",
      "PARCEIRO_GERENTE_ESTRATEGICO",
      "PARCEIRO_GERENTE_VAREJO",
      "PARCEIRO_MASTER"
    ] as const
  ).includes(user.userData.type as any);

  const isPartnerType = (
    [
      "PARCEIRO_GERENTE_ESTRATEGICO",
      "PARCEIRO_GERENTE_VAREJO",
      "PARCEIRO_MASTER"
    ] as const
  ).includes(user.userData.type as any);

  const handleToggle = () => {
    if (isPartnerType) {
      const message = isActive
        ? "Para solicitar o cancelamento do recurso White Label, entre em contato com o seu comercial responsável na Nova Promotora."
        : "Para solicitar a ativação do recurso White Label, entre em contato com o seu comercial responsável na Nova Promotora.";
      openModalConfirm({
        title: "White Label",
        message,
        confirmButtonStyle: { children: "OK" },
        rejectButtonStyle: { display: "none" }
      });
      return;
    }

    if (isActive) {
      openModalConfirm({
        title: "Desativar White Label",
        message: "Deseja desativar o serviço White Label?",
        onConfirm: async () => {
          try {
            const { data } = await apiCarteira.put(
              `/services/white-label/${selectedCustomer.id}`,
              { ativo: false, dominio: cardData?.dominio ?? "" }
            );
            setCardData((prev) => ({ ...prev, whiteLabel: data }));
          } catch (err: any) {
            const msg = err?.response?.data?.msgInterface;
            Toast({
              title: msg ?? "Erro ao desativar White Label",
              status: "error"
            });
          }
        }
      });
    } else {
      openModalConfirm({
        title: "Ativar White Label",
        message: (
          <Flex flexDir="column" gap="10px" w="100%">
            <Text>Informe o domínio/URL (opcional):</Text>
            <Input
              ref={dominioRef}
              placeholder="https://exemplo.com.br"
              defaultValue={cardData?.dominio ?? ""}
              size="sm"
              borderRadius="md"
            />
          </Flex>
        ),
        variant: "normal",
        confirmButtonStyle: { children: "Ativar" },
        onConfirm: async () => {
          try {
            const { data } = await apiCarteira.put(
              `/services/white-label/${selectedCustomer.id}`,
              { ativo: true, dominio: dominioRef.current?.value ?? "" }
            );
            setCardData((prev) => ({ ...prev, whiteLabel: data }));
          } catch (err: any) {
            const msg = err?.response?.data?.msgInterface;
            Toast({
              title: msg ?? "Erro ao ativar White Label",
              status: "error"
            });
          }
        }
      });
    }
  };

  const titleContainerHeight = "80px";

  return (
    <SkeletonLoading
      isLoading={!!isLoading}
      borderRadius="10px"
      bg="#fff"
      overflow="hidden"
    >
      <Flex
        justifyContent="space-between"
        flexDir="column"
        bg="#fff"
        borderRadius="10px"
        overflow="hidden"
        h="100%"
        border="1px solid var(--chakra-colors-custom-gray)"
      >
        <Flex
          px={px}
          w="100%"
          h={titleContainerHeight}
          alignItems="center"
          pos="relative"
          overflow="hidden"
          justifyContent="flex-start"
        >
          <Text
            fontSize={{ base: "18px", "2xl": "20px" }}
            fontWeight="semibold"
          >
            {title}
          </Text>
          <Box pos="absolute" bottom="-20px" right="35px">
            <CardBgIcon />
          </Box>
        </Flex>

        <Flex flexDir="column" px={px} py="15px" minH="215px">
          <Grid
            templateColumns="1fr 2fr"
            gap="10px"
            alignItems="center"
            w="100%"
            mb="10px"
          >
            <Text fontWeight="semibold">Status:</Text>
            <Flex alignItems="center" justifyContent="flex-end">
              <StatusFormat
                mr="10px"
                w="82px"
                bg={isExpired ? bgWarning : isActive ? bgSucess : bgError}
              >
                {isExpired ? "Expirado" : isActive ? "Ativo" : "Inativo"}
              </StatusFormat>
              {canToggle && (
                <Button
                  w="calc(100% - 92px)"
                  leftIcon={<RepeatIcon w="13px" h="13px" />}
                  variant="outline"
                  size="xs"
                  onClick={handleToggle}
                >
                  {isActive ? "Desativar" : "Ativar"}
                </Button>
              )}
            </Flex>
          </Grid>

          {cardData?.expirationDate && (
            <Grid
              templateColumns="1fr 2fr"
              gap="10px"
              alignItems="center"
              w="100%"
              mb="10px"
            >
              <Text fontWeight="semibold" whiteSpace="nowrap">
                Data de Expiração:
              </Text>
              <Flex alignItems="center" justifyContent="flex-end">
                <Text>
                  {cardData.expirationDate.split("-").reverse().join("/")}
                </Text>
              </Flex>
            </Grid>
          )}

          {cardData?.dominio && (
            <Flex flexDir="column" gap="2px">
              <Text fontWeight="semibold">Domínio:</Text>
              <Text
                as="a"
                href={
                  cardData.dominio.startsWith("http")
                    ? cardData.dominio
                    : `https://${cardData.dominio}`
                }
                target="_blank"
                rel="noopener noreferrer"
                fontSize="xs"
                color="blue.500"
                textDecoration="underline"
                isTruncated
                title={cardData.dominio}
              >
                {cardData.dominio}
              </Text>
            </Flex>
          )}
        </Flex>

        <Box px={px} pb="15px" mt="auto">
          <PriceButton>{priceDesc}</PriceButton>
        </Box>
      </Flex>
    </SkeletonLoading>
  );
}
